import React from 'react';
import { Link } from 'react-router-dom';
import TopNav from './TopNav';
import SolutionsFooter from './SolutionsFooter';
import HowToEngage from './HowToEngage';
import useSEO from '../hooks/useSEO';

function RevenuePipelineExample({ logo }) {
  useSEO({
    title: 'Revenue Pipeline Simulation',
    description: 'Simulate pipeline scenarios to understand true revenue probability. Stop guessing which deals will actually close.'
  });

  return (
    <div className="min-h-screen bg-white">
      <TopNav />

      {/* Hero Section */}
      <main className="max-w-5xl mx-auto px-8 py-16 md:py-24">
        <div className="text-center mb-16">
          <p className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-4">Pipeline + Revenue Forecasting</p>
          <h1 className="text-4xl md:text-5xl font-medium text-gray-900 mb-6 leading-tight tracking-tight">
            Stop guessing which deals will actually close
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Simulate pipeline scenarios to understand true revenue probability—before the quarter ends with surprises.
          </p>
        </div>

        {/* Who is this for */}
        <div className="mb-16">
          <h2 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-6 text-center">Who this is for</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 text-center">
              <p className="font-medium text-gray-900 mb-2">Revenue Leaders</p>
              <p className="text-sm text-gray-600">Building reliable forecasts for the board</p>
            </div>
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 text-center">
              <p className="font-medium text-gray-900 mb-2">Sales Operations</p>
              <p className="text-sm text-gray-600">Optimizing capacity and territory allocation</p>
            </div>
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 text-center">
              <p className="font-medium text-gray-900 mb-2">Finance Partners</p>
              <p className="text-sm text-gray-600">Stress-testing revenue assumptions</p>
            </div>
          </div>
        </div>

        {/* What decisions it improves */}
        <div className="mb-16">
          <h2 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-6 text-center">Decisions this improves</h2>
          <div className="bg-white border border-gray-200 rounded-lg p-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="flex items-start">
                <span className="text-gray-400 mr-3 mt-1">—</span>
                <p className="text-gray-700">Which deals are truly at risk of slipping</p>
              </div>
              <div className="flex items-start">
                <span className="text-gray-400 mr-3 mt-1">—</span>
                <p className="text-gray-700">How much pipeline coverage is actually needed</p>
              </div>
              <div className="flex items-start">
                <span className="text-gray-400 mr-3 mt-1">—</span>
                <p className="text-gray-700">Where to allocate rep time for maximum impact</p>
              </div>
              <div className="flex items-start">
                <span className="text-gray-400 mr-3 mt-1">—</span>
                <p className="text-gray-700">When to adjust hiring plans based on capacity</p>
              </div>
              <div className="flex items-start">
                <span className="text-gray-400 mr-3 mt-1">—</span>
                <p className="text-gray-700">What the realistic range of quarterly outcomes is</p>
              </div>
              <div className="flex items-start">
                <span className="text-gray-400 mr-3 mt-1">—</span>
                <p className="text-gray-700">How pricing changes affect close rates</p>
              </div>
            </div>
          </div>
        </div>

        {/* Industry Example */}
        <div className="mb-16">
          <div className="bg-gray-900 text-white rounded-lg p-8 md:p-10">
            <p className="text-sm font-medium text-gray-400 uppercase tracking-wider mb-4">Example: Enterprise B2B Sales</p>
            <p className="text-lg leading-relaxed mb-6">
              An enterprise software company modeled deal velocity by stage, win rates by segment, and rep capacity constraints. 
              The simulation revealed that their "committed" pipeline had a 60% probability of hitting target—not the 85% they had assumed. 
              Early intervention on three key deals changed the outcome.
            </p>
            <p className="text-gray-400 text-sm">
              The impact: 35% improvement in forecast accuracy and $4M in recovered revenue.
            </p>
          </div>
        </div>

                <HowToEngage
          platformDescription="Connect your CRM, model your pipeline dynamics, and run scenarios yourself. Ideal for RevOps teams who want hands-on control."
          consultingDescription="Partner with our consultants to build a custom forecasting model, run quarterly scenario workshops, and deliver executive-ready insights."
        />

        {/* Related Solutions */}
        <div className="pt-8 border-t border-gray-200">
          <h2 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-6">Related solutions</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Link to="/solutions/workforce" className="text-gray-700 hover:text-gray-900 text-sm">
              Capacity + Backlog Planning →
            </Link>
            <Link to="/solutions/financial-services" className="text-gray-700 hover:text-gray-900 text-sm">
              Risk + Portfolio Analysis →
            </Link>
            <Link to="/solutions/retail-ecommerce" className="text-gray-700 hover:text-gray-900 text-sm">
              Demand Forecasting + Pricing →
            </Link>
          </div>
        </div>
      </main>

      <SolutionsFooter />
    </div>
  );
}

export default RevenuePipelineExample;
